import React from 'react';
import {useParams, useHistory} from 'react-router-dom';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import {useChangelog} from '../context/ChangelogContext';
import ChangelogBadge from './ChangelogBadge';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import '../styles/changelog.css';

const ChangelogDetail = () => {
  const {id} = useParams();
  const {allEntries, allLoading} = useChangelog();
  const history = useHistory();

  const entry = allEntries.find((e) => e.id === id);
  const entryIndex = allEntries.findIndex((e) => e.id === id);
  const prevEntry = entryIndex < allEntries.length - 1 ? allEntries[entryIndex + 1] : null;
  const nextEntry = entryIndex > 0 ? allEntries[entryIndex - 1] : null;

  if (allLoading) {
    return (
      <div className="changelog-detail">
        <div className="changelog-loading">Loading...</div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="changelog-detail">
        <button type="button" className="changelog-detail-back" onClick={() => history.push('/changelog')}>
          <ArrowBackRounded /> Back to changelog
        </button>
        <p className="changelog-empty">Entry not found.</p>
      </div>
    );
  }

  const date = entry.publishedAt?.toDate ? entry.publishedAt.toDate() : new Date(entry.publishedAt);
  moment(date).fromNow();
  const fullDate = moment(date).format('MMMM D, YYYY [at] h:mm A');

  // Fix literal \n sequences stored in Firestore
  const description = entry.description ? entry.description.replaceAll('\\n', '\n') : '';

  return (
    <div className="changelog-detail" key={id}>
      <button type="button" className="changelog-detail-back" onClick={() => history.push('/changelog')}>
        <ArrowBackRounded /> Back to changelog
      </button>

      <article>
        <div className="changelog-detail-header">
          {entry.tags && entry.tags.length > 0 && (
            <div className="changelog-detail-badges">
              {entry.tags.map((tag) => (
                <ChangelogBadge key={tag} tag={tag} />
              ))}
            </div>
          )}
          <span className="changelog-detail-time">
            {fullDate}{entry.repository && <> &middot; {entry.repository}</>}
          </span>
          <h1 className="changelog-detail-title">{entry.title}</h1>
        </div>

        {description && (
          <div className="changelog-detail-body markdown-body">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
              components={{
                img: ({node, ...props}) => (
                  <img
                    {...props}
                    style={{maxWidth: '100%', borderRadius: 8}}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ),
              }}
            >
              {description}
            </ReactMarkdown>
          </div>
        )}
      </article>

      <div className="changelog-detail-nav">
        {prevEntry ? (
          <div
            className="changelog-detail-nav-btn changelog-detail-nav-prev"
            onClick={() => history.push(`/changelog/${prevEntry.id}`)}
          >
            <ChevronLeftRounded />
            <div className="changelog-detail-nav-text">
              <span className="changelog-detail-nav-label">Previous</span>
              <span className="changelog-detail-nav-date">
                {moment(prevEntry.publishedAt?.toDate ? prevEntry.publishedAt.toDate() : new Date(prevEntry.publishedAt)).format('MMM D, YYYY')}
              </span>
            </div>
          </div>
        ) : <div />}
        {nextEntry ? (
          <div
            className="changelog-detail-nav-btn changelog-detail-nav-next"
            onClick={() => history.push(`/changelog/${nextEntry.id}`)}
          >
            <div className="changelog-detail-nav-text">
              <span className="changelog-detail-nav-label">Next</span>
              <span className="changelog-detail-nav-date">
                {moment(nextEntry.publishedAt?.toDate ? nextEntry.publishedAt.toDate() : new Date(nextEntry.publishedAt)).format('MMM D, YYYY')}
              </span>
            </div>
            <ChevronRightRounded />
          </div>
        ) : <div />}
      </div>
    </div>
  );
};

export default ChangelogDetail;
